import { Button, Spin, message } from 'antd';
import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  selectedAlertFilters,
  setUploadedFile,
  showScreeningPopup,
  stopBulkScreening,
  stopBulkScreeningLocal
} from '../../../../redux/actions/ns/onDemandScreening';
import { BULK_SCREENING_FLOW } from '../../../constants/nsConstants';
import img_ns_scanning from '../../../../assets/img_ns_scanning.png';
import img_ns_scan_stop from '../../../../assets/img_ns_scan_stop.png';
import img_ns_scan_completed from '../../../../assets/img_ns_scan_completed.png';
import img_ns_scan_error from '../../../../assets/img_ns_scan_error.png';
import {
  ModalContentContainer,
  ModalDesc,
  ModalHeader,
  StyledImg,
  StyledModal,
  StyledProgress,
  ErrorHeader,
  ErrorDesc,
  ModalWrapper
} from './styled';

const OK_BUTTON_LABELS = {
  [BULK_SCREENING_FLOW.SCREEN]: 'Stop Screening',
  [BULK_SCREENING_FLOW.SCREEN_SUCCESS]: 'Check Alerts',
  [BULK_SCREENING_FLOW.PROGRESS_ERROR]: 'Check Alerts'
};

const CANCEL_BUTTON_LABELS = {
  [BULK_SCREENING_FLOW.SCREEN]: 'Close',
  [BULK_SCREENING_FLOW.SCREEN_SUCCESS]: 'Start New Screening',
  [BULK_SCREENING_FLOW.SCREEN_FAILED]: 'Cancel',
  [BULK_SCREENING_FLOW.PROGRESS_ERROR]: 'Cancel'
};

const ScreenModal = ({
  history,
  dispatch,
  onTabChange,
  isShowScreenModal,
  currentScreeningState,
  jobId,
  screeningPercent,
  screenedRecordCount,
  errorMessage,
  stopScreeningCalled
}) => {
  const [isStopState, setIsStopState] = useState(false);

  useEffect(() => {
    // Return to normal screen when the state is stop and screening is completed or getting error.
    if (isStopState) {
      if (currentScreeningState === BULK_SCREENING_FLOW.SCREEN_SUCCESS) {
        message.warning('Screening already completed!!');
        setIsStopState(false);
      } else if (currentScreeningState === BULK_SCREENING_FLOW.PROGRESS_ERROR) {
        message.warning('Something Wrong!!');
        setIsStopState(false);
      }
    }
  }, [currentScreeningState]);

  const secondaryButtonClick = () => {
    if (isStopState) {
      setIsStopState(false);
    } else if (currentScreeningState === BULK_SCREENING_FLOW.SCREEN_FAILED) {
      dispatch(stopBulkScreeningLocal());
    } else if (
      currentScreeningState === BULK_SCREENING_FLOW.SCREEN_SUCCESS ||
      currentScreeningState === BULK_SCREENING_FLOW.PROGRESS_ERROR
    ) {
      dispatch(stopBulkScreeningLocal());
      dispatch(setUploadedFile(null));
    } else {
      dispatch(showScreeningPopup(false));
    }
  };

  const primaryButtonClick = () => {
    if (isStopState) {
      dispatch(stopBulkScreening({ jobId }));
    } else if (currentScreeningState === BULK_SCREENING_FLOW.SCREEN) {
      setIsStopState(true);
    } else if (
      currentScreeningState === BULK_SCREENING_FLOW.SCREEN_SUCCESS ||
      currentScreeningState === BULK_SCREENING_FLOW.PROGRESS_ERROR
    ) {
      dispatch(stopBulkScreeningLocal());
      dispatch(setUploadedFile(null));
      dispatch(
        selectedAlertFilters({
          alertLevel: '',
          sortOrder: 'desc',
          searchValue: '',
          alertStatus: undefined,
          segmentName: undefined,
          alertScore: [],
          refreshCount: 0
        })
      );
      history.replace({
        pathname: history.location.pathname,
        search: '',
        hash: '',
        state: undefined
      });
      onTabChange('4');
    } else {
      dispatch(showScreeningPopup(false));
    }
  };

  const footer = [
    <Button
      key="back"
      disabled={isStopState && stopScreeningCalled}
      onClick={secondaryButtonClick}>
      {isStopState
        ? 'Keep Screening'
        : CANCEL_BUTTON_LABELS[currentScreeningState]}
    </Button>,
    ...(currentScreeningState !== BULK_SCREENING_FLOW.SCREEN_FAILED ||
    isStopState
      ? [
          <Button
            key="stop"
            type="primary"
            disabled={isStopState && stopScreeningCalled}
            onClick={primaryButtonClick}>
            {isStopState ? 'Yes Stop' : OK_BUTTON_LABELS[currentScreeningState]}
          </Button>
        ]
      : [])
  ];

  const renderScreenModal = useMemo(
    () => (
      <ModalContentContainer>
        <StyledImg src={img_ns_scanning} />
        <ModalHeader>
          You currently have a screening running.
          <br />
          Screening the records uploaded
        </ModalHeader>
        <StyledProgress percent={screeningPercent} />
      </ModalContentContainer>
    ),
    [screeningPercent]
  );

  const renderStopModal = useMemo(
    () => (
      <Spin spinning={stopScreeningCalled}>
        <ModalContentContainer>
          <StyledImg src={img_ns_scan_stop} />
          <ModalHeader>
            Are you sure you want to stop the screening?
            <br />
            All current progress will be lost.
          </ModalHeader>
          <StyledProgress percent={screeningPercent} />
        </ModalContentContainer>
      </Spin>
    ),
    [screeningPercent, stopScreeningCalled]
  );

  const renderSuccessModal = useMemo(
    () => (
      <ModalContentContainer>
        <StyledImg src={img_ns_scan_completed} />
        <ModalHeader>Screening Completed</ModalHeader>
        <ModalDesc>
          All {screenedRecordCount} records have been screened successfully.
          <br />
          Check the alerts by clicking the below button or start a new screening
        </ModalDesc>
        <StyledProgress percent={100} />
      </ModalContentContainer>
    ),
    [screeningPercent, screenedRecordCount]
  );

  const renderErrorModal = useMemo(
    () => (
      <ModalContentContainer>
        <StyledImg src={img_ns_scan_error} />
        <ErrorHeader>Screening Error</ErrorHeader>
        <ErrorDesc>{errorMessage}</ErrorDesc>
        <StyledProgress percent={screeningPercent} status="exception" />
      </ModalContentContainer>
    ),
    [screeningPercent, errorMessage]
  );

  const renderProgressErrorModal = useMemo(
    () => (
      <ModalContentContainer>
        <StyledImg src={img_ns_scan_error} />
        <ErrorHeader>Something Wrong!!!</ErrorHeader>
        <ErrorDesc>
          Please check the alert list after sometime for the screening result.
        </ErrorDesc>
        <StyledProgress percent={screeningPercent} status="exception" />
      </ModalContentContainer>
    ),
    [screeningPercent]
  );

  const MODAL_RENDER = useMemo(
    () => ({
      [BULK_SCREENING_FLOW.SCREEN]: renderScreenModal,
      [BULK_SCREENING_FLOW.SCREEN_SUCCESS]: renderSuccessModal,
      [BULK_SCREENING_FLOW.SCREEN_FAILED]: renderErrorModal,
      [BULK_SCREENING_FLOW.PROGRESS_ERROR]: renderProgressErrorModal
    }),
    [
      renderErrorModal,
      renderScreenModal,
      renderSuccessModal,
      renderProgressErrorModal
    ]
  );

  const onCancelModal = () => {
    if (currentScreeningState === BULK_SCREENING_FLOW.SCREEN_FAILED) {
      dispatch(stopBulkScreeningLocal());
    } else if (!stopScreeningCalled) {
      dispatch(showScreeningPopup(false));
    }
  };

  return (
    <StyledModal
      className="on-demand-screening-button-css"
      visible={isShowScreenModal}
      title="Screening Status"
      destroyOnClose={true}
      footer={footer}
      maskClosable={false}
      width="456px"
      onCancel={onCancelModal}
      onOk={primaryButtonClick}>
      <ModalWrapper>
        {isStopState ? renderStopModal : MODAL_RENDER[currentScreeningState]}
      </ModalWrapper>
    </StyledModal>
  );
};

ScreenModal.propTypes = {
  dispatch: PropTypes.func.isRequired,
  onTabChange: PropTypes.func.isRequired,
  isShowScreenModal: PropTypes.bool,
  currentScreeningState: PropTypes.string,
  jobId: PropTypes.number,
  screeningPercent: PropTypes.number,
  screenedRecordCount: PropTypes.number,
  errorMessage: PropTypes.string,
  stopScreeningCalled: PropTypes.bool,
  history: PropTypes.object.isRequired
};

const mapStateToProps = (state) => {
  const {
    bulkScreening: {
      isShowScreenModal,
      currentScreeningState,
      jobId,
      screeningPercent,
      screenedRecordCount,
      errorMessage,
      stopScreeningCalled
    }
  } = state.nsOnDemandScreening;
  return {
    isShowScreenModal,
    currentScreeningState,
    jobId,
    screeningPercent,
    screenedRecordCount,
    errorMessage,
    stopScreeningCalled
  };
};

export default connect(mapStateToProps)(ScreenModal);
